"use client";
import { AlertCircle, CheckCircle, Info, Lightbulb, X, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { cx, focusRing, typeRole } from "../../utils";
import { Button } from "../Button";
import { TextLink } from "../TextLink";

export type BannerVariant = "info" | "success" | "warning" | "error" | "discovery";
export type BannerAppearance = "subtle" | "bold";

export interface BannerAction {
  /** Label for the action button */
  label: string;
  /** Callback when the action is triggered */
  onPress: () => void;
  /** Whether to render as a link-style button */
  asLink?: boolean;
}

export interface BannerProps {
  /** Semantic variant of the banner */
  variant?: BannerVariant;
  /** Visual appearance style */
  appearance?: BannerAppearance;
  /** The main message/title to display */
  title?: ReactNode;
  /** Additional description or content */
  children?: ReactNode;
  /** Whether the banner can be dismissed */
  dismissible?: boolean;
  /** Callback when the banner is dismissed */
  onDismiss?: () => void;
  /** Primary action (rendered as button or link) */
  primaryAction?: BannerAction;
  /** Secondary action (rendered as button or link) */
  secondaryAction?: BannerAction;
  /** Custom icon to override default status icon */
  icon?: ReactNode;
  /** Hide the icon completely */
  hideIcon?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for automation */
  testId?: string;
}

const variantConfig = {
  info: {
    subtle: {
      container: "bg-feedback-info-background border-l-4 border-l-feedback-info-border",
      iconColor: "text-feedback-info-border",
      titleColor: "text-text-primary",
      bodyColor: "text-text-primary",
    },
    bold: {
      container: "bg-feedback-info-border border-feedback-info-border",
      iconColor: "text-text-inverse",
      titleColor: "text-text-inverse",
      bodyColor: "text-text-inverse/90",
    },
    icon: Info,
  },
  success: {
    subtle: {
      container: "bg-feedback-success-background border-l-4 border-l-feedback-success-border",
      iconColor: "text-feedback-success-border",
      titleColor: "text-text-primary",
      bodyColor: "text-text-primary",
    },
    bold: {
      container: "bg-feedback-success-border border-feedback-success-border",
      iconColor: "text-text-inverse",
      titleColor: "text-text-inverse",
      bodyColor: "text-text-inverse/90",
    },
    icon: CheckCircle,
  },
  warning: {
    subtle: {
      container: "bg-feedback-warning-background border-l-4 border-l-feedback-warning-border",
      iconColor: "text-feedback-warning-border",
      titleColor: "text-text-primary",
      bodyColor: "text-text-primary",
    },
    bold: {
      container: "bg-feedback-warning-border border-feedback-warning-border",
      iconColor: "text-text-inverse",
      titleColor: "text-text-inverse",
      bodyColor: "text-text-inverse/90",
    },
    icon: AlertCircle,
  },
  error: {
    subtle: {
      container: "bg-feedback-error-background border-l-4 border-l-feedback-error-border",
      iconColor: "text-feedback-error-border",
      titleColor: "text-text-primary",
      bodyColor: "text-text-primary",
    },
    bold: {
      container: "bg-feedback-error-border border-feedback-error-border",
      iconColor: "text-text-inverse",
      titleColor: "text-text-inverse",
      bodyColor: "text-text-inverse/90",
    },
    icon: XCircle,
  },
  discovery: {
    subtle: {
      container: "bg-background-muted border-l-4 border-l-action-brand",
      iconColor: "text-action-brand",
      titleColor: "text-text-primary",
      bodyColor: "text-text-primary",
    },
    bold: {
      container: "bg-action-brand border-action-brand",
      iconColor: "text-text-inverse",
      titleColor: "text-text-inverse",
      bodyColor: "text-text-inverse/90",
    },
    icon: Lightbulb,
  },
} as const;

/**
 * Banner
 *
 * A prominent message component for displaying important information or system messages.
 * Can be used inline within content sections or positioned at the page level.
 *
 * Inspired by Atlassian's Section Message and Chakra UI's Alert, combining the best
 * features of both: semantic variant colors, multiple appearances, inline actions, and
 * flexible composition.
 *
 * @example
 * ```tsx
 * // Simple info banner
 * <Banner variant="info" title="New features available">
 *   Check out the latest updates to improve your workflow.
 * </Banner>
 *
 * // With actions
 * <Banner
 *   variant="warning"
 *   title="Your trial expires soon"
 *   primaryAction={{ label: "Upgrade now", onPress: () => {} }}
 *   secondaryAction={{ label: "Learn more", onPress: () => {}, asLink: true }}
 * >
 *   Upgrade to continue using premium features.
 * </Banner>
 *
 * // Bold appearance
 * <Banner
 *   variant="error"
 *   appearance="bold"
 *   title="Action required"
 *   dismissible
 *   onDismiss={() => {}}
 * >
 *   Please update your payment information.
 * </Banner>
 * ```
 */
export function Banner({
  variant = "info",
  appearance = "subtle",
  title,
  children,
  dismissible = false,
  onDismiss,
  primaryAction,
  secondaryAction,
  icon,
  hideIcon = false,
  className,
  testId,
}: BannerProps) {
  const variantData = variantConfig[variant];
  const config = variantData[appearance];
  const IconComponent = variantData.icon;

  const hasActions = Boolean(primaryAction || secondaryAction);
  const hasContent = Boolean(title || children);

  return (
    <div
      className={twMerge("flex gap-3 rounded-sm border p-4", config.container, className)}
      role="region"
      aria-live="polite"
      data-testid={testId}
    >
      {/* Icon */}
      {!hideIcon && (
        <div className={twMerge("flex-shrink-0", config.iconColor)} aria-hidden="true">
          {icon || <IconComponent className="h-5 w-5" />}
        </div>
      )}

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {/* Title and description */}
        {hasContent && (
          <div className="flex-1">
            {title && <div className={cx(typeRole("headingSm"), config.titleColor)}>{title}</div>}
            {children && (
              <div className={cx(typeRole("bodySm"), title ? "mt-1" : "", config.bodyColor)}>{children}</div>
            )}
          </div>
        )}

        {/* Actions */}
        {hasActions && (
          <div className="flex flex-wrap items-center gap-3">
            {primaryAction &&
              (primaryAction.asLink ? (
                <TextLink
                  asButton
                  onPress={primaryAction.onPress}
                  variant={appearance === "bold" ? "unstyled" : "default"}
                  className={appearance === "bold" ? "text-text-inverse hover:text-text-inverse/80" : ""}
                >
                  {primaryAction.label}
                </TextLink>
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  onPress={primaryAction.onPress}
                  className={
                    appearance === "bold"
                      ? "bg-background-surface text-text-primary hover:bg-background-surface/90"
                      : ""
                  }
                >
                  {primaryAction.label}
                </Button>
              ))}
            {secondaryAction &&
              (secondaryAction.asLink ? (
                <TextLink
                  asButton
                  onPress={secondaryAction.onPress}
                  variant={appearance === "bold" ? "unstyled" : "default"}
                  className={appearance === "bold" ? "text-text-inverse hover:text-text-inverse/80" : ""}
                >
                  {secondaryAction.label}
                </TextLink>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={secondaryAction.onPress}
                  className={
                    appearance === "bold" ? "border-text-inverse/30 text-text-inverse hover:bg-text-inverse/10" : ""
                  }
                >
                  {secondaryAction.label}
                </Button>
              ))}
          </div>
        )}
      </div>

      {/* Dismiss button */}
      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          className={twMerge(
            "flex h-8 w-8 flex-shrink-0 items-center justify-center self-start rounded-md transition-colors hover:bg-background-modal/10",
            config.iconColor,
            focusRing()
          )}
          aria-label="Dismiss banner"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
